import { useMessageActionContext } from '@/app/providers';
import { StarIcon } from '@/assets/images';
import { Checkbox, CustomButton } from '@/components';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import GroupProgressLoader from '../../../components/Progress/GroupProgressLoader';
import MediaGroupActionDropdown from '../../components/MediaGroupActionDropdown';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';
import DisplayMessages from './DisplayMessages';
import './mediaGroupCard.scss';

const SENDER_CLASS =
   'sender-chat sender-media-group-row ct-rowReverse chat-bubble ct-flex ct-aiEnd ct-gap10 group-media-bubble';
const RECEIVER_CLASS = 'receiver-chat receiver-media-group-row chat-bubble ct-flex ct-gap10 group-media-bubble';
const SENDER_INNER = 'ct-flex ct-flexColEnd ct-relative ct-gap10';
const RECEIVER_INNER = 'ct-flex ct-flexCol ct-relative ct-gap10';

const MediaGroupCard = memo(({ message, activeChatJid }) => {
   const { hideAvatar = false } = useMessageActionContext();
   const messages = useMemo(() => message.messages || [], [message.messages]);
   const lastMessage = messages[messages.length - 1];
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);
   const senderId = getMessageSenderId({ type: 'chats', message: lastMessage });
   const isSender = senderId === getLocalUserId();

   const timestamp = lastMessage.timestamp
      ? formatChatTime(new Date(Number(lastMessage.timestamp)), 'message-card')
      : '';

   const isAllStarred = useMemo(() => messages.every(msg => msg.favouriteStatus === 1), [messages]);
   const groupMsgStatus = useMemo(() => {
      if (messages.some(msg => msg.msgStatus === 3)) return 3;
      return Math.min(...messages.map(msg => msg.msgStatus));
   }, [messages]);

   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const hideAvatarGroup = isSender || hideAvatar;
   const showProfile = isChatGroup && !hideAvatarGroup;

   return (
      <CustomButton
         as="div"
         className={`message-row media-group-row ${isSelectionActive ? 'ct-flex ct-aiCenter selection-active' : ''} ${isSelected ? 'selected' : ''}`}
         onClick={isSelectionActive ? handleToggleSelection : null}
      >
         {isSelectionActive && (
            <div className="selection-checkbox-wrap">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}

         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div className={`${isSender ? SENDER_INNER : RECEIVER_INNER}`}>
               <div
                  className={`chat-box media-group-container ct-flex ct-fcol ct-gap8 ${lastMessage?.msgBody?.replyTo ? 'reply-bubble' : ''}`}
               >
                  {showProfile && <Username userId={senderId} />}
                  {lastMessage?.msgBody?.replyTo && (
                     <ReplyMessageCard replyTo={lastMessage?.msgBody?.replyTo} activeChatJid={activeChatJid} />
                  )}
                  <div className="ct-relative">
                     <DisplayMessages messages={messages} activeChatJid={activeChatJid} />
                     <GroupProgressLoader messages={messages} />
                  </div>
               </div>
               <div className={`group-meta ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={groupMsgStatus} />}
                  <span className="time">{timestamp}</span>
                  {isAllStarred && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>
               <MediaGroupActionDropdown message={message} isSender={isSender} activeChatJid={activeChatJid} />
            </div>
         </div>
      </CustomButton>
   );
});

MediaGroupCard.displayName = 'MediaGroupCard';
MediaGroupCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default MediaGroupCard;
